import { useCallback, useState } from "react";

import type { BundledTranslationChanges } from "../Translations/RenameEntityModal";
import {
  applyListEntityRename,
  applyListEntityTranslations,
} from "../Translations/listTranslationData";
import type { FutureCard, FutureMarketOption, FutureTab } from "./futuresDummyData";
import {
  cloneFuturesListData,
  type FuturesListData,
} from "./futuresTransforms";

// Monotonic suffix for locally created ids — unlike Date.now(), two adds in
// the same millisecond can never collide. Real ids come from the API later.
let idSeq = 0;
function nextLocalId(prefix: string): string {
  idSeq += 1;
  return `${prefix}-${idSeq}`;
}

export interface FuturesDummyState {
  tabs: FutureTab[];
  cardsByTabId: Record<string, FutureCard[]>;
  addTab: (tab: FutureTab) => void;
  renameTab: (
    tabId: string,
    newName: string,
    translationsEnabled?: boolean,
    translationChanges?: BundledTranslationChanges
  ) => void;
  setTabActive: (tabId: string, isActive: boolean) => void;
  deleteTab: (tabId: string) => void;
  reorderTabs: (rows: FutureTab[]) => void;
  addCard: (tabId: string, option: FutureMarketOption) => void;
  deleteCard: (tabId: string, entityId: string) => void;
  reorderCards: (tabId: string, orderedEntityIds: string[]) => void;
  // Replace a tab's card list wholesale (used to revert a failed optimistic
  // mutation to the previous snapshot).
  setTabCards: (tabId: string, cards: FutureCard[]) => void;
}

const EMPTY_SEED: FuturesListData = { tabs: [], cardsByTabId: {} };

// Tabs + cards state shared by the Future Market Tabs and Markets sections.
// Seeded from GET_FUTURES_ENTITIES per league; mutations are applied locally
// (optimistically) while the parent persists them via the write APIs.
export function useFuturesDummyState(
  leagueId: string | null,
  seed: FuturesListData | null = null,
  seedRevision = 0
): FuturesDummyState {
  const [tabs, setTabs] = useState<FutureTab[]>([]);
  const [cardsByTabId, setCardsByTabId] = useState<
    Record<string, FutureCard[]>
  >({});

  // Reseed whenever the league or the fetched payload revision changes.
  // Adjust state during render (not in an effect) so the reseed lands in the
  // same commit as the league/data change; a post-commit effect would briefly
  // expose the previous league's state.
  // null revision forces the first render to apply `seed` even when the
  // caller's revision starts at 0 / dataUpdatedAt.
  const [seededLeagueId, setSeededLeagueId] = useState(leagueId);
  const [seededRevision, setSeededRevision] = useState<number | null>(null);
  if (leagueId !== seededLeagueId || seedRevision !== seededRevision) {
    setSeededLeagueId(leagueId);
    setSeededRevision(seedRevision);
    const next = cloneFuturesListData(seed ?? EMPTY_SEED);
    setTabs(next.tabs);
    setCardsByTabId(next.cardsByTabId);
  }

  const addTab = useCallback((tab: FutureTab) => {
    setTabs((prev) => [
      ...prev,
      {
        ...tab,
        order: tab.order || String(prev.length + 1),
      },
    ]);
  }, []);

  const renameTab = useCallback(
    (
      tabId: string,
      newName: string,
      translationsEnabled = false,
      translationChanges?: BundledTranslationChanges
    ) => {
      setTabs((prev) =>
        prev.map((tab) => {
          if (tab.id !== tabId) return tab;
          const renamed = applyListEntityRename(
            tab,
            newName,
            translationsEnabled,
            "ename"
          );
          if (!translationChanges) return renamed;
          return applyListEntityTranslations(
            renamed,
            translationChanges.translations,
            "ename",
            translationChanges.translation_key
          );
        })
      );
    },
    []
  );

  const setTabActive = useCallback((tabId: string, isActive: boolean) => {
    setTabs((prev) =>
      prev.map((tab) =>
        tab.id === tabId ? { ...tab, is_active: isActive } : tab
      )
    );
  }, []);

  const deleteTab = useCallback((tabId: string) => {
    setTabs((prev) =>
      prev
        .filter((tab) => tab.id !== tabId)
        .map((tab, idx) => ({ ...tab, order: String(idx + 1) }))
    );
    setCardsByTabId((prev) => {
      const { [tabId]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const reorderTabs = useCallback((rows: FutureTab[]) => {
    setTabs(rows);
  }, []);

  const addCard = useCallback((tabId: string, option: FutureMarketOption) => {
    setCardsByTabId((prev) => ({
      ...prev,
      [tabId]: [
        ...(prev[tabId] ?? []),
        {
          entity_id: nextLocalId(option.entity_id),
          market_id: option.market_id,
          display_name: option.name,
          original_name: option.name,
          specifier: option.specifier,
        },
      ],
    }));
  }, []);

  const deleteCard = useCallback((tabId: string, entityId: string) => {
    setCardsByTabId((prev) => ({
      ...prev,
      [tabId]: (prev[tabId] ?? []).filter(
        (card) => card.entity_id !== entityId
      ),
    }));
  }, []);

  const setTabCards = useCallback((tabId: string, cards: FutureCard[]) => {
    setCardsByTabId((prev) => ({ ...prev, [tabId]: cards }));
  }, []);

  const reorderCards = useCallback(
    (tabId: string, orderedEntityIds: string[]) => {
      setCardsByTabId((prev) => {
        const cards = prev[tabId] ?? [];
        const byId = new Map(cards.map((card) => [card.entity_id, card]));
        const reordered = orderedEntityIds
          .map((id) => byId.get(id))
          .filter((card): card is FutureCard => Boolean(card));
        // A mismatched id list means the DOM order can't be trusted.
        if (reordered.length !== cards.length) return prev;
        return { ...prev, [tabId]: reordered };
      });
    },
    []
  );

  return {
    tabs,
    cardsByTabId,
    addTab,
    renameTab,
    setTabActive,
    deleteTab,
    reorderTabs,
    addCard,
    deleteCard,
    reorderCards,
    setTabCards,
  };
}
